import {Card, Col, Row, Spin} from 'antd'
import {AccountBean, EventStatKeyColumnDescription, EventStatValueColumnDescription} from 'common/api/api.ts'
import {useUserContext} from 'common/context/UserContext.tsx'
import DashboardHeader from 'common/pages/dashboard/DashboardHeader'
import {Format, toTimezone} from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import {useEffect, useState} from 'react'

import Filters from './Filters.tsx'
import HorizontalBarChart from './HorizontalBarChart.tsx'
import PerformanceChart from './PerformanceChart.tsx'
import {DashboardFilters, useDashboardData} from './UseDashboardData.ts'

const KeyColumnEnum = EventStatKeyColumnDescription.ColumnEnum
const ValueColumnEnum = EventStatValueColumnDescription.ColumnEnum
const RoleEnum = AccountBean.RoleEnum

const buildTopBarTitles = (metricLabel: string) => ({
  [KeyColumnEnum.Advertiser]: `Top 10 Advertisers by ${metricLabel}`,
  [KeyColumnEnum.Campaign]: `Top 10 Campaigns by ${metricLabel}`,
  [KeyColumnEnum.Ssp]: `Top 10 SSPs by ${metricLabel}`,
  [KeyColumnEnum.Creative]: `Top 10 Creatives by ${metricLabel}`,
  [KeyColumnEnum.AdGroup]: `Top 10 AdGroups by ${metricLabel}`,
  [KeyColumnEnum.Country]: `Top 10 Countries by ${metricLabel}`,
})

export const Dashboard = ({ agency }) => {
  const { user, colorPalette } = useUserContext()
  const [screenWidth, setScreenWidth] = useState<number | null>(null)
  const [activeFilter, setActiveFilter] = useState<[dayjs.Dayjs, dayjs.Dayjs, boolean, boolean]>(() => {
    const now = toTimezone(dayjs(), user?.timezoneId)
    return [now.startOf('day'), now.endOf('day'), false, false]
  })
  const [filters, setFilters] = useState<DashboardFilters>({})

  const { performanceData, topValuesData, loadPerformanceData, loadTopData, loading } = useDashboardData(
    activeFilter,
    filters,
  )

  const getNames = (el: any) => el.name
  const getValues = (el: any) => el.value as number

  const topBars = agency
    ? [KeyColumnEnum.Advertiser, KeyColumnEnum.Campaign, KeyColumnEnum.Ssp]
    : [KeyColumnEnum.Creative, KeyColumnEnum.AdGroup, KeyColumnEnum.Country]
  const hasNetCostAccess = user?.role === RoleEnum.Admin
  const topBarValueColumn = hasNetCostAccess ? ValueColumnEnum.CostNet : ValueColumnEnum.CostGross
  const topBarTitles = buildTopBarTitles(hasNetCostAccess ? 'Cost Net' : 'Cost')

  useEffect(() => {
    const updateWidth = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', updateWidth)
    updateWidth()

    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadTopData(topBars, topBarValueColumn), loadPerformanceData()])
    }

    loadData()
  }, [filters, activeFilter])

  useEffect(() => {
    if (user?.timezoneId) {
      const now = toTimezone(dayjs(), user?.timezoneId)
      setActiveFilter([now.startOf('day'), now.endOf('day'), true, false])
    }
  }, [user?.timezoneId])

  if (screenWidth === null) {
    return null
  }

  return (
    <div style={{ background: '#F0F2F5', display: 'flex', gap: '16px' }}>
      <div style={{ width: 'calc(100% - 300px)' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <DashboardHeader
              performanceData={performanceData}
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
              loading={loading}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {performanceData ? (
            <PerformanceChart performanceData={performanceData} loading={loading} />
          ) : (
            <Card style={{ borderRadius: 8, background: '#FFFFFF', width: '100%', margin: 8, minHeight: '380px' }}>
              <Spin
                size="large"
                style={{ display: 'block', margin: '20px', minHeight: '380px', alignContent: 'center' }}
              />
            </Card>
          )}
        </Row>

        <Row gutter={[8, 8]}>
          <Col xs={24} md={8}>
            <HorizontalBarChart
              title={topBarTitles[topBars[0]]}
              labels={topValuesData[topBars[0]]?.map(getNames)}
              data={topValuesData[topBars[0]]?.map(getValues)}
              color={colorPalette['--color-primary-2'] ?? '#5888CF'}
              max={topValuesData[topBars[0]] && Math.max(...topValuesData[topBars[0]].map(getValues))}
              loading={loading}
              format={Format.Cost}
            />
          </Col>
          <Col xs={24} md={8}>
            <HorizontalBarChart
              title={topBarTitles[topBars[1]]}
              labels={topValuesData[topBars[1]]?.map(getNames)}
              data={topValuesData[topBars[1]]?.map(getValues)}
              color={colorPalette['--color-primary-1'] ?? '#334D97'}
              max={topValuesData[topBars[1]] && Math.max(...topValuesData[topBars[1]].map(getValues))}
              loading={loading}
              format={Format.Cost}
            />
          </Col>
          <Col xs={24} md={8}>
            <HorizontalBarChart
              title={topBarTitles[topBars[2]]}
              labels={topValuesData[topBars[2]]?.map(getNames)}
              data={topValuesData[topBars[2]]?.map(getValues)}
              color={colorPalette['--color-secondary-1'] ?? '#FFA914'}
              max={topValuesData[topBars[2]] && Math.max(...topValuesData[topBars[2]].map(getValues))}
              loading={loading}
              format={Format.Cost}
            />
          </Col>
        </Row>
      </div>

      <Filters setFilters={setFilters} filters={filters} admin={agency} />
    </div>
  )
}
